import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    CalendarDays,
    Users,
    ScanLine,
    User as UserIcon,
    Briefcase,
    UsersRound,
    Settings2,
    ArrowLeft,
    Menu,
    X,
    ChevronDown,
    Check,
} from "lucide-react";
import { useRole } from "@/lib/roleContext";
import { useAuth } from "@/lib/authContext";
import { Wordmark } from "@/components/Wordmark";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RoleSwitcher from "@/components/RoleSwitcher";

const ALL_ITEMS = [
    { to: "/workspace", label: "Overview", icon: LayoutDashboard, end: true, roles: ["artist_owner", "artist_team", "admin"] },
    { to: "/workspace/events", label: "Events", icon: CalendarDays, roles: ["artist_owner", "artist_team", "event_staff", "admin"] },
    { to: "/workspace/audience", label: "Audience", icon: Users, roles: ["artist_owner", "artist_team", "admin"] },
    { to: "/workspace/check-in", label: "Check-In", icon: ScanLine, roles: ["artist_owner", "artist_team", "event_staff", "admin"] },
    { to: "/workspace/artist-page", label: "Artist Page", icon: UserIcon, roles: ["artist_owner", "artist_team", "admin"] },
    { to: "/workspace/business", label: "Business", icon: Briefcase, roles: ["artist_owner", "admin"] },
    { to: "/workspace/team", label: "Team", icon: UsersRound, roles: ["artist_owner", "artist_team", "admin"] },
    { to: "/workspace/settings", label: "Settings", icon: Settings2, roles: ["artist_owner", "artist_team", "admin"] },
];

// Human-readable label for the platform authority the account itself
// carries — distinct from any workspace membership.
const PLATFORM_ROLE_LABEL = {
    admin: "Platform Admin",
};

// Human-readable label for a real artist_members relationship. Note:
// we deliberately don't include a fallback so a Platform Admin viewing
// an artist workspace via admin authority never displays as "Artist
// Owner" simply because they can operate the workspace.
const MEMBERSHIP_LABEL = {
    owner: "Artist Owner",
    manager: "Artist Manager",
    team: "Artist Team",
    staff: "Event Staff",
};

export default function WorkspaceNav() {
    const {
        artists = [],
        activeArtistId,
        activeArtistName,
        activeArtist,
        setActiveArtist,
        role,
        globalRole,
        membershipRole,
    } = useRole();
    const { user, venues, logout: _logout } = useAuth();
    const [open, setOpen] = useState(false);

    const items = ALL_ITEMS.filter((it) => it.roles.includes(role));

    // Platform authority (belongs to the account) vs. workspace relationship
    // (belongs to this artist). These are ALWAYS surfaced separately —
    // Admin viewing an artist workspace shows an ADMIN VIEW indicator.
    // NOTE: `/api/auth/artists` injects a synthetic membership role of
    // "admin" for every artist a Platform Admin can see, so we filter that
    // out when deriving the actual workspace relationship. A real
    // relationship for an admin (they also happen to own the artist) is
    // still captured via artist_members and will surface as owner/manager
    // /team/staff on the same row.
    const platformLabel = PLATFORM_ROLE_LABEL[globalRole] || "User";
    const isPlatformAdmin = globalRole === "admin";
    const realMembershipRole = membershipRole && membershipRole !== "admin" ? membershipRole : null;
    const membershipLabel = realMembershipRole ? MEMBERSHIP_LABEL[realMembershipRole] : null;
    const isAdminView = isPlatformAdmin && !realMembershipRole;
    const workspaceRelationshipLabel = membershipLabel || (isAdminView ? "Admin view" : "—");

    const onSwitchArtist = (a) => setActiveArtist(a.id, a.name);

    return (
        <>
            <div className="md:hidden sticky top-0 z-40 bg-[#0a0a0a] border-b border-[#2a2a2a] px-4 h-14 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => setOpen(!open)} data-testid="ws-mobile-toggle" className="p-2 border border-[#2a2a2a]">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    <Wordmark small />
                </div>
                <div className="text-xs font-mono text-[#a3a3a3] uppercase">Workspace</div>
            </div>

            <aside
                className={`bg-[#0a0a0a] border-r border-[#2a2a2a] w-full md:w-[240px] md:fixed md:top-0 md:bottom-0 md:left-0 md:z-40 md:flex md:flex-col ${
                    open ? "block" : "hidden md:block"
                }`}
                data-testid="workspace-sidebar"
            >
                <div className="hidden md:flex items-center h-16 px-6 border-b border-[#2a2a2a]">
                    <Link to="/" className="flex items-center gap-2">
                        <Wordmark small />
                    </Link>
                </div>

                <div className="px-6 py-4 border-b border-[#2a2a2a]">
                    <div className="text-[10px] uppercase tracking-widest text-[#666] font-mono">Active Workspace</div>
                    {artists.length > 1 ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger className="w-full mt-1 flex items-center justify-between text-left group" data-testid="ws-artist-switcher">
                                <span className="font-display font-bold text-lg leading-tight truncate">{activeArtistName || "—"}</span>
                                <ChevronDown className="w-4 h-4 text-[#666] group-hover:text-[#ff4400]" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#141414] border-[#2a2a2a] rounded-none text-[#f4f4f0] w-64">
                                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-[#666]">Switch artist</DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                                {artists.map((a) => (
                                    <DropdownMenuItem
                                        key={a.id}
                                        onClick={() => onSwitchArtist(a)}
                                        data-testid={`ws-artist-option-${a.id}`}
                                        className="cursor-pointer rounded-none focus:bg-[#1f1f1f] focus:text-[#ff4400]"
                                    >
                                        <span className="flex-1 font-display font-bold uppercase tracking-wide text-sm truncate">{a.name}</span>
                                        {a.id === activeArtistId && <Check className="w-4 h-4 text-[#ff4400]" />}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="mt-1 font-display font-bold text-lg leading-tight">{activeArtistName || "—"}</div>
                    )}
                </div>

                <nav className="flex-1 py-3">
                    {items.map((it) => {
                        const Icon = it.icon;
                        return (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                end={it.end}
                                onClick={() => setOpen(false)}
                                data-testid={`ws-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-6 py-3 text-sm font-display font-bold uppercase tracking-wider border-l-2 transition-colors ${
                                        isActive ? "border-[#ff4400] text-[#ff4400] bg-[#141414]" : "border-transparent text-[#f4f4f0] hover:text-[#ff4400] hover:bg-[#141414]"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" />
                                {it.label}
                            </NavLink>
                        );
                    })}
                    {venues && venues.length > 0 && (
                        <Link to={`/venue/${venues[0].id}/overview`} onClick={() => setOpen(false)} data-testid="ws-nav-venue" className="flex items-center gap-3 px-6 py-3 mt-2 text-sm font-display font-bold uppercase tracking-wider border-l-2 border-transparent text-[#ff9500] hover:text-[#ff4400] hover:bg-[#141414]">
                            <Briefcase className="w-4 h-4" /> Venue workspace →
                        </Link>
                    )}
                </nav>

                <div className="border-t border-[#2a2a2a] px-6 py-4 space-y-3 min-w-0" data-testid="ws-footer">
                    {/* SIGNED-IN ACCOUNT — platform identity, distinct from the workspace being viewed */}
                    <div className="min-w-0" data-testid="ws-signed-in">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Signed in as</div>
                        <div className="text-xs font-mono truncate min-w-0 mt-0.5" data-testid="ws-user-email">{user?.email}</div>
                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-0.5" data-testid="ws-platform-role">{platformLabel}</div>
                    </div>

                    {/* ACTIVE WORKSPACE — the artist being viewed + this user's real relationship to it */}
                    {activeArtistId && (
                        <div className="min-w-0 pt-2 border-t border-[#2a2a2a]" data-testid="ws-active-workspace">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Viewing workspace</div>
                            <div className="text-xs font-display font-bold uppercase truncate min-w-0 mt-0.5" data-testid="ws-workspace-name">
                                {activeArtistName || activeArtist?.name || "—"}
                            </div>
                            <div
                                className={`text-[10px] font-mono uppercase mt-0.5 ${isAdminView ? "text-[#ff9500]" : "text-[#a3a3a3]"}`}
                                data-testid="ws-workspace-relationship"
                            >
                                {workspaceRelationshipLabel}
                            </div>
                            {isAdminView && (
                                <div
                                    className="inline-block mt-1 px-1.5 py-0.5 border border-[#ff9500] text-[9px] font-mono uppercase tracking-widest text-[#ff9500]"
                                    data-testid="ws-admin-view-indicator"
                                    title="You are viewing this workspace through Platform Admin authority — you are not an actual member of this artist."
                                >
                                    Admin view
                                </div>
                            )}
                        </div>
                    )}

                    {isPlatformAdmin && (
                        <div className="pt-2 border-t border-[#2a2a2a]">
                            <div className="text-[10px] font-mono uppercase text-[#666] mb-1">Dev impersonation</div>
                            <RoleSwitcher fullWidth />
                        </div>
                    )}
                    <Link to="/" className="mt-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3] hover:text-[#ff4400] min-w-0" data-testid="ws-back-public">
                        <ArrowLeft className="w-3 h-3 shrink-0" /> Back to Project Mint
                    </Link>
                </div>
            </aside>
        </>
    );
}
